import { SuggestItems } from '@doda/common/src/interfaces/suggestDispatcher';
import {
  MouseEvent,
  useState,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react';
import { KEYWORD_OPTIONS } from 'components/KeywordSuggest/constants';
import { getSuggestDispatcher } from '@doda/common/src/services/suggestDispatcherAPI';
import {
  ExcludedKeywordCategoryResponseCode,
  IncludedKeywordCategoryRequestCode,
  IncludedKeywordCategoryResponseCode,
} from '@doda/api-client/aspida/@types';
import { tl } from '@doda/common/src/utils/adobeAnalyticsUtil';
import { createInitialSearchText } from 'logics/createInitialSearchText';
import {
  DEFAULT_SEARCH_CATEGORY,
  MIN_INPUT_LENGTH,
} from 'constants/searchCondition';

/**
 * キーワードに関するhooks
 * @param isAdobeTagFired Adobe計測用のフラグ
 * @param setIsAdobeTagFired Adobe計測用のフラグの更新関数
 * @param initialCategory
 * @param initialKeyword
 * @returns キーワードの値と更新する関数を含むオブジェクト
 */
export const useSearchKeyword = (
  isAdobeTagFired: boolean,
  setIsAdobeTagFired: Dispatch<SetStateAction<boolean>>,
  initialCategory:
    | IncludedKeywordCategoryResponseCode
    | ExcludedKeywordCategoryResponseCode
    | undefined,
  initialKeyword: string[] | undefined
) => {
  const [searchText, setSearchText] = useState(
    createInitialSearchText(initialKeyword)
  );
  const [suggests, setSuggests] = useState<SuggestItems['items']>([]);
  const [searchCategory, setSearchCategory] =
    useState<IncludedKeywordCategoryRequestCode>(
      initialCategory ?? DEFAULT_SEARCH_CATEGORY
    );

  const onChangeText = useCallback(
    (inputText: string) => {
      setSearchText(inputText);

      if (inputText.length < MIN_INPUT_LENGTH) {
        setSuggests([]);
        return;
      }

      if (inputText.length >= MIN_INPUT_LENGTH) {
        // 1PVにつき一度のみ発火
        if (!isAdobeTagFired) {
          tl('FW_SRCHsgt_imp', {});
          setIsAdobeTagFired(true);
        }
        // 大量のアクションが発行されそうなので、直接レスポンスを使う
        getSuggestDispatcher(
          KEYWORD_OPTIONS.find((item) => item.value === searchCategory)?.type ||
            KEYWORD_OPTIONS[0].type,
          inputText
        )
          .then((res) => {
            setSuggests(res);
          })
          .catch();
      }
    },
    [searchCategory, isAdobeTagFired, setIsAdobeTagFired]
  );

  const onClickSuggest = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setSearchText(event.currentTarget.value);
    setSuggests([]);
  }, []);

  const onChangeUpdateKeyword = useCallback(
    (value: IncludedKeywordCategoryRequestCode) => {
      setSearchCategory(value);
    },
    []
  );

  const onFocusInput = useCallback(() => {
    if (searchText?.length >= MIN_INPUT_LENGTH) {
      getSuggestDispatcher(
        KEYWORD_OPTIONS.find((item) => item.value === searchCategory)?.type ||
          KEYWORD_OPTIONS[0].type,
        searchText
      )
        .then((res) => {
          setSuggests(res);
        })
        .catch();
    }
  }, [searchText, searchCategory]);

  const resetSuggests = useCallback(() => {
    setSuggests([]);
  }, []);

  // 選択状態クリア処理
  const clearIsSelectedKeyword = () => {
    setSearchText('');
    setSearchCategory(DEFAULT_SEARCH_CATEGORY);
  };

  return {
    state: {
      searchText,
      suggests,
      searchCategory,
    },
    updateFunctions: {
      onChangeText,
      onClickSuggest,
      onChangeUpdateKeyword,
      onFocusInput,
      resetSuggests,
      clearIsSelectedKeyword,
    },
  };
};
